import React, { useMemo, useState } from 'react'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetIntegrationEntitiesQuery } from '../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../common/integrations'

import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'

export const RedtailCategoryActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const accountId = useAppSelector(selectAccountId)

  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.REDTAIL, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.REDTAIL,
    sourcePlatform: 'redtail',
    sourceObject: 'contact',
    entityType: 'update_contact',
    relatedToType: 'contact',
    successToast: { title: 'Assigning category', message: 'Redtail category assignment started.' },
    errorToast: { title: 'Failed to assign category', message: 'Could not start the Redtail category assignment. Please try again.' },
  })

  const { data, isFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_categories',
  })

  const categories: SelectOption[] = useMemo(() => {
    const contactCategories = data?.data?.contactCategories ?? []
    return contactCategories.map((category) => ({ id: String(category.id), label: category.name }))
  }, [data])

  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(null)

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'}`

  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Redtail' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Redtail' />
    ) : (
      <div className='d-flex flex-column gap-4'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <SelectDropdown
          className='py-2 zocks-px-0.75 zocks-gap-3'
          options={categories}
          initialSelectedOption={selectedCategory}
          onOptionSelected={setSelectedCategory}
          placeholder='Select a category to assign'
          loading={isFetching}
          searchable
        />
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='assign a category to' crmName='Redtail' />
      </div>
    )

  const handleSave = async () => {
    if (!selectedCategory) {
      return
    }
    await save({ contact: { category_id: selectedCategory.id } })
  }

  const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !selectedCategory

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Assign category to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Assign category'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
